import { Injectable } from '@angular/core';
import { Path } from '../../shared/path';
import jwt_decode from 'jwt-decode';
import { MicrosoftAccessTokenPayloadClaims, MicrosoftAuth, MicrosoftIdTokenPayloadClaims } from './onedrive.type';

declare const microsoft: string;

@Injectable()
export class OneDrive {
   private clientId = microsoft;
   private storedAppInfo: any = null;
   private scope = 'files.readwrite+user.read+email+openid+profile';
   action: any;
   private openPopup: Window;
   private redirectUri = Path.getAppUrl('callback');

   signIn(act: 'signin'|'create'|'view'|'share'|'file') {
      this.action = act;
      const appInfo = {
         'clientId': this.clientId,
         'redirectUri': this.redirectUri,
         'scopes': this.scope,
         'authServiceUri': 'https://login.microsoftonline.com/common/oauth2/v2.0/authorize'
      };
      this.provideAppInfo(appInfo);
      // use Microsoft Graph v1.0
      const baseUrl = this.getQueryVariable('baseUrl');
      const msGraphApiRoot = (baseUrl) ? baseUrl : 'https://graph.microsoft.com/v1.0/me';

      localStorage.setItem('onedrive_action', this.action);
      this.challengeForAuth();
      return false;
   }

   provideAppInfo(obj: any) {
      this.storedAppInfo = obj;
   }

   getQueryVariable(variable: any) {
      const query = window.location.search.substring(1);
      const vars = query.split('&');
      for (let i = 0; i < vars.length; i++) {
         const pair = vars[i].split('=');
         if (pair[0] === variable) {return pair[1]; }
      }
      return(false);
   }

   challengeForAuth() {
      const appInfo = this.getAppInfo();
      // https://learn.microsoft.com/en-us/entra/identity-platform/v2-oauth2-implicit-grant-flow#send-the-sign-in-request
      let url =
             appInfo.authServiceUri +
             '?client_id=' + appInfo.clientId +
             '&response_type=token+id_token' +
             '&redirect_uri=' + encodeURIComponent(appInfo.redirectUri);

      if (appInfo.scopes) {
         url = url + '&scope=' + appInfo.scopes;
      }
      if (appInfo.resourceUri) {
         url = url + '&resource=' + encodeURIComponent(appInfo.resourceUri);
      }
      // add current time as nonce to input param
      url = url + '&nonce=' + encodeURIComponent(new Date().getTime().toString());

      this.popup(url);
   }

   getAppInfo() {

      if (this.storedAppInfo) {
         return this.storedAppInfo;
      }

      const scriptTag = document.getElementById('odauth');
      if (!scriptTag) {
         alert('the script tag for odauth.js should have its id set to "odauth"');
      }

      const clientId = scriptTag.getAttribute('clientId');
      if (!clientId) {
         alert('the odauth script tag needs a clientId attribute set to your application id');
      }

      const scopes = scriptTag.getAttribute('scopes');
      // scopes aren't always required, so we don't warn here.

      const redirectUri = scriptTag.getAttribute('redirectUri');
      if (!redirectUri) {
         alert('the odauth script tag needs a redirectUri attribute set to your redirect landing url');
      }

      const resourceUri = scriptTag.getAttribute('resourceUri');

      const authServiceUri = scriptTag.getAttribute('authServiceUri');
      if (!authServiceUri) {
         alert('the odauth script tag needs an authServiceUri attribtue set to the oauth authentication service url');
      }

      const appInfo = {
         'clientId': clientId,
         'scopes': scopes,
         'redirectUri': redirectUri,
         'resourceUri': resourceUri,
         'authServiceUri': authServiceUri
      };

      this.storedAppInfo = appInfo;

      return appInfo;
   }

   popup(url: any) {
      const width = 525,
            height = 525,
            screenX = window.screenX,
            screenY = window.screenY,
            outerWidth = window.outerWidth,
            outerHeight = window.outerHeight;

      const left = screenX + Math.max(outerWidth - width, 0) / 2;
      const top = screenY + Math.max(outerHeight - height, 0) / 2;

      const features = [
         'width=' + width,
         'height=' + height,
         'top=' + top,
         'left=' + left,
         'status=no',
         'resizable=yes',
         'toolbar=no',
         'menubar=no',
         'scrollbars=yes'];
      const popup = window.open(url, 'oauth', features.join(','));
      this.openPopup = popup;
      if (!popup) {
         alert('failed to pop up auth window');
      }

      popup.focus();
   }

   saveToCookie(obj: any) {
      const expiration = new Date();
      expiration.setTime(expiration.getTime() + 3600 * 1000);
      const data = JSON.stringify(obj);
      let cookie = 'odexplorer=' + data + '; path=/; expires=' + expiration.toUTCString();
      if (document.location.protocol.toLowerCase() === 'https') {
         cookie = cookie + ';secure';
      }
      document.cookie = cookie;
   }

   /** 
    * Decode value from Microsoft JWT.
    * 
    * For details of the token, see:
    * - https://learn.microsoft.com/en-us/entra/identity-platform/access-token-claims-reference
    * - https://learn.microsoft.com/en-us/entra/identity-platform/id-token-claims-reference
    * - https://jwt.ms
    */
   getProfile(token: MicrosoftAuth['access_token'] | MicrosoftAuth['id_token']): MicrosoftAccessTokenPayloadClaims | MicrosoftIdTokenPayloadClaims {
      let decoded: MicrosoftAccessTokenPayloadClaims | MicrosoftIdTokenPayloadClaims;
      if (token) {
         try {
            decoded = jwt_decode(token);
         } catch {
            return;
         }
      }
      return decoded;
   }

   waitCallback(): boolean {
      const value = Boolean(localStorage.getItem('onedrive_callback'));
      return !value && !this.openPopup.closed;
   }

   isCallback(): boolean {
      return Boolean(localStorage.getItem('onedrive_callback'));
   }
}
